import { useCallback, useState } from "react";
import {
  fetchSpotifyCategories,
  searchSpotifyPlaylists,
  fetchPlaylistDetails,
} from "../services/outreach/integrations/spotify";
import type {
  SpotifyCategory,
  SpotifyPlaylistSearchResult,
  SpotifyPlaylistDetails,
} from "../types/outreach";

interface UseSpotifyPlaylistSearchReturn {
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  activeQuery: string | null;
  categories: SpotifyCategory[];
  categoriesLoading: boolean;
  selectedCategoryId: string | null;
  playlists: SpotifyPlaylistSearchResult[];
  selectedPlaylist: SpotifyPlaylistDetails | null;
  loading: boolean;
  detailsLoading: boolean;
  error: string | null;
  fetchCategories: (force?: boolean) => Promise<void>;
  selectCategory: (categoryId: string, categoryName: string) => Promise<void>;
  performSearch: (
    query?: string,
    options?: { resetCategory?: boolean }
  ) => Promise<void>;
  fetchPlaylistDetailsById: (playlistId: string) => Promise<void>;
  setSelectedPlaylist: (playlist: SpotifyPlaylistDetails | null) => void;
  resetSearch: () => void;
}

const normalizeError = (error: unknown): string => {
  if (error instanceof Error) {
    const message = error.message.toLowerCase();

    if (message.includes("token") || message.includes("unauthorized")) {
      return "Spotify authentication failed. Please verify your API credentials.";
    }

    if (message.includes("rate") || message.includes("429")) {
      return "Spotify rate limit reached. Please wait a moment and try again.";
    }

    if (message.includes("network")) {
      return "Network error while contacting Spotify. Check your connection and try again.";
    }

    return error.message;
  }

  return "An unexpected error occurred while communicating with Spotify.";
};

export const useSpotifyPlaylistSearch = (): UseSpotifyPlaylistSearchReturn => {
  const [searchQuery, setSearchQuery] = useState("");
  const [activeQuery, setActiveQuery] = useState<string | null>(null);
  const [categories, setCategories] = useState<SpotifyCategory[]>([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(
    null
  );
  const [playlists, setPlaylists] = useState<SpotifyPlaylistSearchResult[]>([]);
  const [selectedPlaylist, setSelectedPlaylist] =
    useState<SpotifyPlaylistDetails | null>(null);
  const [loading, setLoading] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchCategories = useCallback(
    async (force?: boolean) => {
      if (!force && categories.length > 0) {
        return;
      }

      setCategoriesLoading(true);
      setError(null);

      try {
        const result = await fetchSpotifyCategories();
        setCategories(result);
      } catch (err) {
        console.error("Spotify category fetch failed:", err);
        setError(normalizeError(err));
        setCategories([]);
      } finally {
        setCategoriesLoading(false);
      }
    },
    [categories.length]
  );

  const performSearch = useCallback(
    async (query?: string, options?: { resetCategory?: boolean }) => {
      const effectiveQuery = (query ?? searchQuery).trim();

      if (!effectiveQuery) {
        setPlaylists([]);
        setActiveQuery(null);
        setSelectedPlaylist(null);
        if (options?.resetCategory) {
          setSelectedCategoryId(null);
        }
        return;
      }

      setLoading(true);
      setError(null);
      setSelectedPlaylist(null);

      try {
        const result = await searchSpotifyPlaylists(effectiveQuery, 50);
        setPlaylists(result);
        setActiveQuery(effectiveQuery);
        if (options?.resetCategory) {
          setSelectedCategoryId(null);
        }
      } catch (err) {
        console.error("Spotify playlist search failed:", err);
        setError(normalizeError(err));
        setPlaylists([]);
        setActiveQuery(null);
        if (options?.resetCategory) {
          setSelectedCategoryId(null);
        }
      } finally {
        setLoading(false);
      }
    },
    [searchQuery]
  );

  const selectCategory = useCallback(
    async (categoryId: string, categoryName: string) => {
      setSelectedCategoryId(categoryId);
      setSearchQuery(categoryName);
      await performSearch(categoryName);
    },
    [performSearch]
  );

  const fetchPlaylistDetailsById = useCallback(async (playlistId: string) => {
    setDetailsLoading(true);
    setError(null);

    try {
      const details = await fetchPlaylistDetails(playlistId);
      setSelectedPlaylist(details);
    } catch (err) {
      console.error("Spotify playlist detail fetch failed:", err);
      setError(normalizeError(err));
    } finally {
      setDetailsLoading(false);
    }
  }, []);

  const resetSearch = useCallback(() => {
    setSearchQuery("");
    setActiveQuery(null);
    setSelectedCategoryId(null);
    setPlaylists([]);
    setSelectedPlaylist(null);
    setError(null);
  }, []);

  return {
    searchQuery,
    setSearchQuery,
    activeQuery,
    categories,
    categoriesLoading,
    selectedCategoryId,
    playlists,
    selectedPlaylist,
    loading,
    detailsLoading,
    error,
    fetchCategories,
    selectCategory,
    performSearch,
    fetchPlaylistDetailsById,
    setSelectedPlaylist,
    resetSearch,
  };
};
